import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { fmt } from '../utils/format';
import { compressImage, fileToBase64, getMediaType } from '../utils/imageUtils';

const CATEGORIES = [
  { value: 'fuel', label: '⛽ Yakıt' },
  { value: 'seed', label: '🌱 Tohum' },
  { value: 'fertilizer', label: '🧪 Gübre' },
  { value: 'pesticide', label: '💧 İlaç' },
  { value: 'material', label: '🔧 Malzeme' },
  { value: 'other', label: '📦 Diğer' },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));
const UNITS = ['kg', 'ton', 'litre', 'adet', 'çuval', 'dekar'];

const CONF = {
  high: { label: '✅ Yüksek Güven', color: '#2d5a27', bg: '#e8f5e4' },
  medium: { label: '⚠️ Orta Güven', color: '#8a6d00', bg: '#fff8e1' },
  low: { label: '❓ Düşük Güven', color: '#c0392b', bg: '#fdecea' },
};
const TIPS = ['🧠 Ürünler tanınıyor...', '💰 Fiyatlar hesaplanıyor...', '📅 Tarih okunuyor...', '✅ Neredeyse bitti...'];
const TR_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
function fmtDate(s) {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  return `${String(d).padStart(2, '0')} ${TR_MONTHS[m - 1]} ${y}`;
}

export default function ReceiptScanner({ onSuccess, onClose, onManualEntry }) {
  const [step, setStep] = useState('capture'); // capture | processing | review | success | error
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanId, setScanId] = useState(null);
  const [items, setItems] = useState([]);
  const [vendorName, setVendorName] = useState('');
  const [receiptDate, setReceiptDate] = useState('');
  const [confidence, setConfidence] = useState('medium');
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [errorMsg, setErrorMsg] = useState('');
  const [savedCount, setSavedCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [tipIdx, setTipIdx] = useState(0);

  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  useEffect(() => {
    if (step !== 'processing') return;
    const t = setInterval(() => setTipIdx(i => (i + 1) % TIPS.length), 2000);
    return () => clearInterval(t);
  }, [step]);

  const pick = (f) => {
    if (!f) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const scan = async () => {
    if (!file) return;
    setStep('processing');
    try {
      const compressed = await compressImage(file, 2);
      const imageBase64 = await fileToBase64(compressed);
      const mediaType = getMediaType(compressed);
      const r = await api.post('/receipts/scan', { imageBase64, mediaType });
      const { scanId: sid, extracted, error } = r.data;
      setScanId(sid);

      if (error && (!extracted || !extracted.items || extracted.items.length === 0)) {
        setErrorMsg(error);
        setStep('error');
        return;
      }
      const its = (extracted.items || []);
      if (its.length === 0) {
        setErrorMsg('Bu görüntüde fiş bulunamadı. Manuel giriş yapar mısınız?');
        setStep('error');
        return;
      }
      setItems(its.map(it => ({ ...it, include: true })));
      setVendorName(extracted.vendorName || '');
      setReceiptDate(extracted.receiptDate || new Date().toISOString().split('T')[0]);
      setConfidence(extracted.confidenceOverall || 'medium');
      if (extracted.languageDetected && extracted.languageDetected !== 'tr') {
        toast('⚠️ Yabancı dil fişi — lütfen kontrol edin', { icon: '🌐' });
      }
      setStep('review');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || err.response?.data?.error || 'Fiş okunamadı');
      setStep('error');
    }
  };

  const setItem = (i, patch) => setItems(arr => arr.map((it, j) => {
    if (j !== i) return it;
    const next = { ...it, ...patch };
    const q = parseFloat(next.quantity), up = parseFloat(next.unitPrice);
    if (!isNaN(q) && !isNaN(up)) next.totalPrice = q * up;
    return next;
  }));

  const includedItems = items.filter(i => i.include);
  const total = includedItems.reduce((s, i) => s + (parseFloat(i.totalPrice) || (parseFloat(i.quantity) || 0) * (parseFloat(i.unitPrice) || 0)), 0);

  const save = async () => {
    if (includedItems.length === 0) return toast.error('En az bir kalem seçin');
    setSaving(true);
    try {
      const r = await api.post('/receipts/confirm', {
        scanId,
        vendorName, receiptDate, paymentStatus,
        items: items.map(i => ({
          include: i.include, name: i.name, category: i.category,
          quantity: i.quantity, unit: i.unit, unitPrice: i.unitPrice,
          totalAmount: i.totalPrice,
        })),
      });
      setSavedCount(r.data.count);
      setStep('success');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null); setPreviewUrl(null); setScanId(null); setItems([]);
    setVendorName(''); setReceiptDate(''); setErrorMsg(''); setEditIdx(null);
    setStep('capture');
  };

  return (
    <div style={overlay}>
      <div style={sheet}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#1c3a1c' }}>📷 Fiş Tara</h2>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#888' }}>✕</button>
        </div>

        {/* hidden inputs */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment"
          style={{ display: 'none' }} onChange={e => pick(e.target.files[0])} />
        <input ref={galleryRef} type="file" accept="image/*"
          style={{ display: 'none' }} onChange={e => pick(e.target.files[0])} />

        {step === 'capture' && (
          <div>
            {!file ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button onClick={() => cameraRef.current.click()} style={bigBtn}>
                  <div style={{ fontSize: '2.2rem' }}>📷</div>
                  <div style={{ fontWeight: 700 }}>Fotoğraf Çek</div>
                  <div style={{ fontSize: '0.78rem', color: '#7a9472' }}>(Kamera aç)</div>
                </button>
                <button onClick={() => galleryRef.current.click()} style={bigBtn}>
                  <div style={{ fontSize: '2.2rem' }}>🖼️</div>
                  <div style={{ fontWeight: 700 }}>Galeriden Seç</div>
                  <div style={{ fontSize: '0.78rem', color: '#7a9472' }}>(Dosya seç)</div>
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <img src={previewUrl} alt="fiş" style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 12, border: '1px solid #eee' }} />
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={reset}>Değiştir</button>
                  <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={scan}>📤 Fişi Tara</button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'processing' && (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            {previewUrl && <img src={previewUrl} alt="fiş" style={{ maxHeight: 160, borderRadius: 12, border: '1px solid #eee', marginBottom: 16 }} />}
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1c3a1c' }}>🔍 Fiş okunuyor...</div>
            <div style={{ marginTop: 10, fontSize: '0.9rem', color: '#7a9472', minHeight: 22 }}>{TIPS[tipIdx]}</div>
            <div className="spinner" style={{ margin: '16px auto', width: 32, height: 32, border: '3px solid #e8f5e4', borderTopColor: 'var(--green-mid)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {step === 'review' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontWeight: 700, color: '#1c3a1c' }}>Fiş Tanındı — Kontrol Edin</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: 20, color: CONF[confidence].color, background: CONF[confidence].bg }}>
                {CONF[confidence].label}
              </span>
            </div>

            {/* Vendor + date (editable) */}
            <div className="form-row">
              <div className="form-group"><label>🏪 Satıcı</label>
                <input value={vendorName} onChange={e => setVendorName(e.target.value)} placeholder="Satıcı adı" /></div>
              <div className="form-group"><label>📅 Tarih</label>
                <input type="date" value={receiptDate} onChange={e => setReceiptDate(e.target.value)} /></div>
            </div>

            {/* Items */}
            <div style={{ marginTop: 6 }}>
              {items.map((it, i) => {
                const c = CONF[it.confidence] || CONF.medium;
                const editing = editIdx === i;
                return (
                  <div key={i} style={{
                    border: `1px solid ${it.confidence === 'low' ? '#f0d090' : '#eee'}`,
                    background: it.confidence === 'low' ? '#fffcf2' : '#fff',
                    borderRadius: 12, padding: 10, marginBottom: 8, opacity: it.include ? 1 : 0.5,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <input type="checkbox" checked={it.include} onChange={e => setItem(i, { include: e.target.checked })}
                        style={{ width: 18, height: 18, marginTop: 2 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 700 }}>{it.name || 'İsimsiz'}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#888' }}>{CAT_LABEL[it.category] || it.category}</span>
                        </div>
                        <div style={{ fontSize: '0.84rem', color: '#444', marginTop: 2 }}>
                          {it.quantity} {it.unit} × {fmt(it.unitPrice || 0)} = <b>{fmt(it.totalPrice || (it.quantity || 0) * (it.unitPrice || 0))}</b>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: c.color, marginTop: 2 }}>{c.label}</div>
                        {editing && (
                          <div style={{ marginTop: 8, background: '#f9f9f9', borderRadius: 8, padding: 8 }}>
                            <div className="form-group"><label>Ad</label>
                              <input value={it.name} onChange={e => setItem(i, { name: e.target.value })} /></div>
                            <div className="form-row">
                              <div className="form-group"><label>Kategori</label>
                                <select value={it.category} onChange={e => setItem(i, { category: e.target.value })}>
                                  {CATEGORIES.map(c2 => <option key={c2.value} value={c2.value}>{c2.label}</option>)}
                                </select></div>
                              <div className="form-group"><label>Birim</label>
                                <select value={it.unit} onChange={e => setItem(i, { unit: e.target.value })}>
                                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                </select></div>
                            </div>
                            <div className="form-row">
                              <div className="form-group"><label>Miktar</label>
                                <input type="number" step="0.01" value={it.quantity} onChange={e => setItem(i, { quantity: e.target.value })} /></div>
                              <div className="form-group"><label>Birim Fiyat (₺)</label>
                                <input type="number" step="0.01" value={it.unitPrice || ''} onChange={e => setItem(i, { unitPrice: e.target.value })} /></div>
                            </div>
                          </div>
                        )}
                        <button onClick={() => setEditIdx(editing ? null : i)}
                          style={{ marginTop: 6, background: '#f0f0f0', border: 'none', borderRadius: 8, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>
                          {editing ? 'Bitti' : 'Düzenle'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div style={{ background: '#e8f5e4', borderRadius: 12, padding: 12, marginTop: 4 }}>
              <div style={{ fontWeight: 800, color: '#1c3a1c' }}>Toplam: {fmt(total)} · {includedItems.length} kalem</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => setPaymentStatus('paid')} style={payBtn(paymentStatus === 'paid')}>✅ Ödendi</button>
                <button onClick={() => setPaymentStatus('pending')} style={payBtn(paymentStatus === 'pending')}>⏳ Bekliyor</button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={reset}>🔄 Yeniden Tara</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving || includedItems.length === 0} onClick={save}>
                {saving ? 'Kaydediliyor...' : `✅ Kaydet (${includedItems.length} kalem)`}
              </button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ fontSize: '2.4rem' }}>✅</div>
            <div style={{ fontWeight: 800, color: '#1c3a1c', fontSize: '1.1rem', marginBottom: 10 }}>{savedCount} alım kaydedildi!</div>
            <div style={{ textAlign: 'left', marginBottom: 14 }}>
              {items.filter(i => i.include).map((it, i) => (
                <div key={i} style={{ fontSize: '0.84rem', padding: '4px 0', borderBottom: '1px solid #f2f2f2' }}>
                  {it.name} · {it.quantity} {it.unit} · <b>{fmt(it.totalPrice || (it.quantity || 0) * (it.unitPrice || 0))}</b>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => onSuccess && onSuccess()}>📋 Alımlarda Görüntüle</button>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={reset}>📷 Başka Fiş Tara</button>
            </div>
          </div>
        )}

        {step === 'error' && (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ fontSize: '2.2rem' }}>❌</div>
            <div style={{ fontWeight: 700, color: '#c0392b', marginBottom: 8 }}>{errorMsg || 'Fiş okunamadı'}</div>
            <div style={{ textAlign: 'left', fontSize: '0.82rem', color: '#7a9472', margin: '0 auto 14px', maxWidth: 280 }}>
              <div>• Görüntü çok bulanık olabilir</div>
              <div>• Fiş çok karanlık olabilir</div>
              <div>• Desteklenmeyen format olabilir</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={reset}>🔄 Tekrar Dene</button>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => onManualEntry && onManualEntry()}>✏️ Manuel Gir</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' };
const sheet = { background: '#fff', width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 16, boxShadow: '0 -8px 30px rgba(0,0,0,0.2)' };
const bigBtn = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '24px 16px', border: '2px dashed var(--border)', borderRadius: 16, background: '#fafdf8', cursor: 'pointer', color: '#1c3a1c' };
const payBtn = (active) => ({ flex: 1, padding: '8px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem', background: active ? 'var(--green-mid)' : '#fff', color: active ? '#fff' : '#555', border: active ? '1px solid var(--green-mid)' : '1px solid #ddd' });
